import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { purchasesQueryParam } from "../lib/purchases";
import Paywall from "../components/Paywall";
import SafeImage from "../components/SafeImage";
import AuthorCard from "../components/AuthorCard";

export default function ArticleDetail() {
  const { slug } = useParams();
  const { t, tr, lang } = useI18n();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sessions = purchasesQueryParam();
    const qs = sessions ? `?sessions=${encodeURIComponent(sessions)}` : "";
    api.get(`/articles/${slug}${qs}`)
       .then((r) => setA(r.data))
       .catch(() => setA(null))
       .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="gai-ivory-surface min-h-screen flex items-center justify-center">{t("common.loading")}</div>;
  if (!a) return <div className="gai-ivory-surface min-h-screen flex items-center justify-center">404</div>;

  const categoryLabels = {
    fr: { finance: "Finance", politique: "Politique", "socio-culturel": "Socio-culturel", interview: "Interview", top: "Classement" },
    en: { finance: "Finance", politique: "Politics", "socio-culturel": "Socio-cultural", interview: "Interview", top: "Ranking" },
  };

  const isLocked = a.is_premium && !a.has_access;
  const fullBody = tr(a.body) || "";
  const previewBody = isLocked ? (tr(a.preview_excerpt) || tr(a.excerpt)) : "";
  const body = isLocked ? previewBody : fullBody;
  const paragraphs = body.split(/\n\n+/).filter(Boolean);

  const fmt = (n, c) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <article className="gai-ivory-surface min-h-screen" data-testid="page-article-detail">
      <div className="border-b" style={{ borderColor: "rgba(5,16,36,0.12)" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-10">
          <Link to="/articles" className="inline-flex items-center gap-2 text-[0.75rem] tracking-[0.2em] uppercase hover:text-[var(--gai-red)]" data-testid="article-back">
            <ArrowLeft size={14} /> {t("articles.title")}
          </Link>
          <div className="mt-8 flex items-center gap-4 flex-wrap text-[0.72rem] tracking-[0.22em] uppercase">
            <span className="text-[var(--gai-red)]">{categoryLabels[lang]?.[a.category] || a.category}</span>
            <span style={{ color: "rgba(5,16,36,0.55)" }}>·</span>
            <span style={{ color: "rgba(5,16,36,0.7)" }}>{a.author}</span>
            {a.is_premium && (
              <>
                <span style={{ color: "rgba(5,16,36,0.55)" }}>·</span>
                <span className="inline-flex items-center gap-1.5 px-2 py-1" style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}>
                  <Lock size={11} /> Premium {fmt(a.price, a.currency)}
                </span>
              </>
            )}
          </div>
          <h1 className="serif mt-4 text-4xl md:text-6xl leading-[1.05]" style={{ color: "var(--gai-black)" }}>{tr(a.title)}</h1>
          <p className="mt-6 text-xl leading-relaxed max-w-2xl" style={{ color: "rgba(5,16,36,0.72)" }}>{tr(a.excerpt)}</p>
        </div>
      </div>

      {(
        <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-10">
          <div className="relative">
            <div className="absolute -inset-2 border" style={{ borderColor: "var(--gai-grey-300)" }} />
            <SafeImage
              src={a.cover_url}
              alt={tr(a.title)}
              fallbackLabel={a.category || "GAI"}
              loading="eager"
              className="relative w-full max-h-[70vh] object-cover"
              style={{ minHeight: "320px" }}
            />
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-16 reading-body">
        {paragraphs.length === 0 ? (
          <p style={{ color: "rgba(5,16,36,0.6)" }}>—</p>
        ) : (
          paragraphs.map((p, i) => (
            <p key={i} className={i === 0 ? "gai-dropcap" : ""}>{p}</p>
          ))
        )}

        {isLocked && (
          <Paywall
            article={a}
            magazineId={null}
            magazinePrice={0}
          />
        )}

        <AuthorCard author={a.author} />

        <div className="mt-12 border-t" style={{ borderColor: "var(--gai-grey-300)" }}>
          <p className="mt-6 text-[0.72rem] tracking-[0.2em] uppercase" style={{ color: "rgba(5,16,36,0.55)" }}>
            © GLOBAL AFRIQUE INTERNATIONAL — {a.author}
          </p>
        </div>
      </div>
    </article>
  );
}
